import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  ConfigProvider,
  Form,
  Input,
  Space,
  Button,
  message,
} from 'antd';
import { TreeSelect } from 'antd';
import plPL from 'antd/lib/locale/pl_PL';
import { modulesList } from 'enums/modulesDefinition';
import 'moment-timezone';
import { createUser } from '../request/userRequest';
import { mapModules } from '../../../helpers/moduleMapper';

const checkPasswordValidity = (value: string) => {
  const isNonWhiteSpace = /^\S*$/;
  if (!isNonWhiteSpace.test(value)) {
    return Promise.reject(new Error('Hasło nie może zawierać spacji!'));
  }

  const isContainsUppercase = /^(?=.*[A-Z]).*$/;
  if (!isContainsUppercase.test(value)) {
    return Promise.reject(
      new Error('Hasło musi zawierać co najmniej jedną wielką literę!')
    );
  }

  const isContainsLowercase = /^(?=.*[a-z]).*$/;
  if (!isContainsLowercase.test(value)) {
    return Promise.reject(
      new Error('Hasło musi zawierać co najmniej jedną małą literę!')
    );
  }

  const isContainsNumber = /^(?=.*[0-9]).*$/;
  if (!isContainsNumber.test(value)) {
    return Promise.reject(
      new Error('Hasło musi zawierać co najmniej jedną cyfrę!')
    );
  }

  const isContainsSymbol = /^(?=.*[~`!@#$%^&*()--+={}\[\]|\\:;"'<>,.?/_₹]).*$/;
  if (!isContainsSymbol.test(value)) {
    return Promise.reject(
      new Error('Hasło musi zawierać co najmniej jeden symbol specjalny.')
    );
  }

  const isValidLength = /^.{12,35}$/;
  if (!isValidLength.test(value)) {
    return Promise.reject(
      new Error('Hasło powinno mieć więcej niż 12 znaków!')
    );
  }

  if (value.replace(/\s/g, '').length < 12) {
    return Promise.reject(
      new Error('Hasło powinno mieć więcej niż 12 znaków!')
    );
  }
  return null;
};

const AddUser = (): JSX.Element => {
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);
  const [modulesSelected, onChange] = useState<any>('');
  const [form] = Form.useForm();

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const onSubmitForm = async (values: any) => {
    const mappedValues = {
      ...values,
      modules: JSON.stringify(values.modules),
    };
    toggleButtonLoading(true);
    values.dateAdd = moment.utc(values.dateAdd).format();
    await createUser(mappedValues)
      .then((response) => {
        toggleButtonLoading(false);
        if (response.code === 1) {
          form.resetFields();
          message.success('Pomyślnie utworzono użytkownika');
        }
      })
      .catch(() => {
        toggleButtonLoading(false);
        message.error('Nie można utworzyć użytkownika');
      });
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Ustawienia Administratorów</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/user/add">Administratorzy +</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <div className="site-layout-background">
        <ConfigProvider locale={plPL}>
          <Form
            layout="vertical"
            validateMessages={validateMessages}
            onFinish={onSubmitForm}
            form={form}
          >
            <Form.Item
              name="name"
              label="Nazwa użytkownika"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="username"
              label="Login"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="password"
              label="Hasło"
              rules={[
                {
                  required: true,
                },
                {
                  validator: async (_, value) => {
                    if (value) {
                      if (checkPasswordValidity(value)) {
                        return checkPasswordValidity(value);
                      }
                    }
                  },
                },
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="confirm"
              label="Potwerdź hasło"
              dependencies={['password']}
              rules={[
                {
                  required: true,
                  message: 'Proszę potwierdzić hasło!',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Hasła się nie zgadzają!'));
                  },
                }),
              ]}
            >
              <Input.Password />
            </Form.Item>
            <Form.Item
              name="modules"
              label="Moduły"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <TreeSelect
                showSearch
                value={modulesSelected}
                dropdownStyle={{
                  maxHeight: 400,
                }}
                placeholder="Wybierz uprawnienia"
                allowClear
                multiple
                treeDefaultExpandAll
                virtual={false}
                onChange={(data) => onChange(data)}
              >
                {modulesList.map((el) => mapModules(el))}
              </TreeSelect>
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={isSaveButtonLoading}
                >
                  Zapisz
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </ConfigProvider>
      </div>
    </>
  );
};

export default AddUser;
